import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api.js';
import { usePublicConfig } from '../../config/demo.js';
import { TOKEN_STORAGE_KEY } from '../../config/index.js';
import { useAuth } from '../../context/AuthContext.jsx';

const PHONE_RE = /^\+63 ?9\d{2}[ -]?\d{3}[ -]?\d{4}$/;
const CONNECTORS = [
  { value: 'CCS2', label: 'CCS Type 2 (Combo)' },
  { value: 'Type2', label: 'Type 2 AC Mennekes' },
  { value: 'CHAdeMO', label: 'CHAdeMO' },
  { value: 'GBT', label: 'GB/T' },
];
const ROLE_LABELS = {
  driver: 'Registered EV Driver', admin: 'Administrator', noc: 'NOC Operator', field_technician: 'Field Technician',
  customer_care: 'Customer Care', finance: 'Finance', guest: 'Guest', user: 'Standard User',
};
const EMPTY_VEHICLE = { make: '', model: '', year: '', licensePlate: '', batteryCapacityKwh: '', connectorType: 'CCS2' };
const strongPassword = (v) => v.length >= 8 && /[A-Z]/.test(v) && /\d/.test(v) && /[^A-Za-z0-9]/.test(v);

function Flash({ flash, id }) {
  if (!flash) return null;
  return <div id={id} className={`alert alert-${flash.type} py-2 small`} role={flash.type === 'danger' ? 'alert' : 'status'}>{flash.text}</div>;
}

function VehicleForm({ initial, onSave, onCancel }) {
  const [v, setV] = useState({ ...EMPTY_VEHICLE, ...initial });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e.target.value }));
  const cls = (k) => `form-control${errors[k] ? ' is-invalid' : ''}`;

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!v.make.trim()) next.make = 'Manufacturer is required.';
    if (!v.model.trim()) next.model = 'Model is required.';
    const kwh = Number(v.batteryCapacityKwh);
    if (!(kwh > 0 && kwh <= 300)) next.batteryCapacityKwh = 'Enter a capacity between 0 and 300 kWh.';
    if (v.year !== '' && v.year != null && !(Number(v.year) >= 1990 && Number(v.year) <= new Date().getFullYear() + 1)) {
      next.year = 'Enter a valid model year.';
    }
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    setServerError('');
    try {
      await onSave({
        make: v.make.trim(),
        model: v.model.trim(),
        year: v.year === '' || v.year == null ? null : Number(v.year),
        licensePlate: (v.licensePlate || '').trim() || null,
        batteryCapacityKwh: kwh,
        connectorType: v.connectorType,
      });
    } catch (err) {
      setServerError(err.message);
      setSaving(false);
    }
  };

  return (
    <form id="vehicle-form" noValidate onSubmit={submit} className="border rounded p-3 mb-3 bg-light">
      {serverError && <div className="alert alert-danger py-2 small" role="alert">{serverError}</div>}
      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="veh-make" className="form-label small fw-bold">Manufacturer</label>
          <input id="veh-make" className={cls('make')} value={v.make} onChange={set('make')} placeholder="Hyundai" />
          <div className="invalid-feedback">{errors.make}</div>
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="veh-model" className="form-label small fw-bold">Model</label>
          <input id="veh-model" className={cls('model')} value={v.model} onChange={set('model')} placeholder="Ioniq 5" />
          <div className="invalid-feedback">{errors.model}</div>
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="veh-year" className="form-label small fw-bold">Model Year</label>
          <input id="veh-year" type="number" className={cls('year')} value={v.year ?? ''} onChange={set('year')} placeholder="2025" />
          <div className="invalid-feedback">{errors.year}</div>
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="veh-battery" className="form-label small fw-bold">Battery Capacity (kWh)</label>
          <input id="veh-battery" type="number" step="0.1" className={cls('batteryCapacityKwh')} value={v.batteryCapacityKwh} onChange={set('batteryCapacityKwh')} placeholder="77.4" />
          <div className="invalid-feedback">{errors.batteryCapacityKwh}</div>
        </div>
        <div className="col-md-4 mb-3">
          <label htmlFor="veh-connector" className="form-label small fw-bold">Connector Type</label>
          <select id="veh-connector" className="form-select" value={v.connectorType} onChange={set('connectorType')}>
            {CONNECTORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="veh-plate" className="form-label small fw-bold">License Plate (optional)</label>
          <input id="veh-plate" className="form-control" value={v.licensePlate ?? ''} onChange={set('licensePlate')} placeholder="ABC 1234" />
        </div>
      </div>
      <button type="submit" id="btn-save-vehicle" className="btn btn-success btn-sm fw-bold me-2" disabled={saving}>
        <i className="fa fa-check me-1" aria-hidden="true" /> Save Vehicle
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Cancel</button>
    </form>
  );
}

export default function ProfilePage() {
  const { notificationsSimulated } = usePublicConfig();
  const { updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', sms: true, email: true, push: true });
  const [errors, setErrors] = useState({});
  const [profileFlash, setProfileFlash] = useState(null);
  const [vehicleFlash, setVehicleFlash] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | vehicle
  const [pw, setPw] = useState({ current: '', next: '', confirm: '' });
  const [pwErrors, setPwErrors] = useState({});
  const [pwFlash, setPwFlash] = useState(null);

  const apply = (p) => {
    setProfile(p);
    setForm({ name: p.name, phone: p.phone || '', ...p.notifications });
  };

  useEffect(() => {
    api.getProfile().then(apply).catch((e) => setLoadError(e.message));
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = true;
    if (form.phone.trim() && !PHONE_RE.test(form.phone.trim())) next.phone = true;
    setErrors(next);
    setProfileFlash(null);
    if (Object.keys(next).length) return;
    try {
      const p = await api.updateProfile({
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        notifications: { sms: form.sms, email: form.email, push: form.push },
      });
      apply(p);
      updateUser({ name: p.name });
      setProfileFlash({ type: 'success', text: 'Profile changes saved.' });
    } catch (err) {
      setProfileFlash({ type: 'danger', text: err.message });
    }
  };

  const saveVehicle = async (body) => {
    if (editing && editing !== 'new') await api.updateVehicle(editing.id, body);
    else await api.addVehicle(body);
    apply(await api.getProfile());
    setEditing(null);
    setVehicleFlash({ type: 'success', text: 'Vehicle saved.' });
  };

  const removeVehicle = async (v) => {
    try {
      await api.deleteVehicle(v.id);
      apply(await api.getProfile());
      setVehicleFlash({ type: 'success', text: 'Vehicle removed.' });
    } catch (err) {
      setVehicleFlash({ type: 'danger', text: err.message });
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    const next = {};
    if (!pw.current) next.current = 'Current password is required.';
    if (!strongPassword(pw.next)) next.next = 'Min 8 characters with an uppercase letter, a number and a symbol.';
    if (pw.confirm !== pw.next) next.confirm = 'Passwords must match.';
    setPwErrors(next);
    setPwFlash(null);
    if (Object.keys(next).length) return;
    try {
      const res = await api.changePassword({ currentPassword: pw.current, newPassword: pw.next });
      if (res.token) localStorage.setItem(TOKEN_STORAGE_KEY, res.token);
      setPw({ current: '', next: '', confirm: '' });
      setPwFlash({ type: 'success', text: 'Password updated. Other devices will be signed out.' });
    } catch (err) {
      setPwFlash({ type: 'danger', text: err.message });
    }
  };

  if (loadError) return <div className="container py-5"><div className="alert alert-danger" role="alert">{loadError}</div></div>;
  if (!profile) return <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status" /></div>;

  const field = (id, label, node, error) => (
    <div className="mb-3">
      <label htmlFor={id} className="form-label small fw-bold">{label}</label>
      {node}
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );

  return (
    <div className="container-fluid py-4" id="profile-page">
      <div className="row mb-4">
        <div className="col-12 col-md-8">
          <h1 className="h3 fw-bold mb-1 text-primary"><i className="fa fa-user-gear me-2" aria-hidden="true" />Profile &amp; Vehicle Settings</h1>
          <p className="text-muted small mb-0">Manage personal contact details, notification preferences, vehicle charging profiles, and account security.</p>
        </div>
        {/* Mobile only: the header's Logout button is hidden below lg, so the Account tab lands here. */}
        <div className="col-12 d-lg-none mt-3">
          <button type="button" className="btn btn-outline-danger w-100" id="btn-mobile-logout" onClick={() => { logout(); navigate('/login'); }}>
            <i className="fas fa-sign-out-alt me-2" aria-hidden="true" />Log out
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-lg-6 mb-4">
          <div className="card p-4 mb-4 shadow-sm border bg-white">
            <h2 className="h5 fw-bold mb-3 text-primary"><i className="fa fa-id-card me-2" aria-hidden="true" />Personal Contact Information</h2>
            <Flash flash={profileFlash} id="profile-flash" />
            <form id="profile-info-form" noValidate onSubmit={saveProfile}>
              {field('prof-fullname', 'Full Name',
                <input id="prof-fullname" className={`form-control${errors.name ? ' is-invalid' : ''}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />,
                'Full name is required.')}
              <div className="row">
                <div className="col-md-6">
                  {field('prof-email', 'Email Address', <input id="prof-email" type="email" className="form-control bg-light" value={profile.email} readOnly />)}
                </div>
                <div className="col-md-6">
                  {field('prof-phone', 'Mobile Phone (+63)',
                    <input id="prof-phone" type="tel" className={`form-control${errors.phone ? ' is-invalid' : ''}`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+63 917 555 8899" />,
                    'Valid Philippine mobile number required (+63).')}
                </div>
              </div>
              <div className="mb-3">
                <span className="form-label small fw-bold d-block">Account Role</span>
                <span className="badge bg-primary px-3 py-2 fw-bold">{ROLE_LABELS[profile.role] || profile.role}</span>
              </div>
              <fieldset className="mb-4">
                <legend className="form-label small fw-bold">Charging milestone notifications</legend>
                {[['sms', 'SMS'], ['email', 'Email'], ['push', 'Push']].map(([k, label]) => (
                  <div className="form-check form-switch form-check-inline" key={k}>
                    <input className="form-check-input" type="checkbox" role="switch" id={`notif-${k}`} checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} />
                    <label className="form-check-label small" htmlFor={`notif-${k}`}>{label}</label>
                  </div>
                ))}
                <div className="form-text" id="notif-note">
                  {notificationsSimulated
                    ? 'Your choices are saved. Demo: SMS, email and push messages are not delivered in this environment.'
                    : 'Your choices are saved and used when a message provider is connected.'}
                </div>
              </fieldset>
              <button type="submit" id="btn-save-profile" className="btn btn-primary fw-bold btn-sm"><i className="fa fa-save me-1" aria-hidden="true" /> Save Changes</button>
            </form>
          </div>

          <div className="card p-4 shadow-sm border bg-white">
            <h2 className="h5 fw-bold mb-3 text-primary"><i className="fa fa-id-badge me-2" aria-hidden="true" />RFID / NFC Tap-to-Charge Keyfob</h2>
            <p className="text-muted small mb-3">The RFID card on file for your account. Tap-to-charge at the charger is not active in this demo: start a charge by scanning the charger&apos;s QR code or from the app. Contact an administrator to link or replace a card.</p>
            <div className="p-3 rounded bg-light border">
              <span className="text-muted small d-block">UID Identifier</span>
              <strong className="font-monospace text-dark" id="user-rfid-tag">{profile.rfidTag || 'No card linked'}</strong>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card p-4 mb-4 shadow-sm border bg-white">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 className="h5 fw-bold mb-0 text-primary"><i className="fa fa-car me-2" aria-hidden="true" />Registered EV Vehicles</h2>
              {!editing && <button type="button" id="btn-add-vehicle" className="btn btn-outline-success btn-sm fw-bold" onClick={() => setEditing('new')}><i className="fa fa-plus me-1" aria-hidden="true" />Add Vehicle</button>}
            </div>
            <p className="text-muted small mb-3">Enables charging time and cost estimates, and compatible plug matching in Station Locator.</p>
            <Flash flash={vehicleFlash} id="vehicle-flash" />
            {editing && (
              <VehicleForm
                key={editing === 'new' ? 'new' : editing.id}
                initial={editing === 'new' ? null : { ...editing, year: editing.year ?? '', licensePlate: editing.licensePlate ?? '' }}
                onSave={saveVehicle}
                onCancel={() => setEditing(null)}
              />
            )}
            {profile.vehicles.length === 0 && !editing && <p className="text-muted small mb-0" id="no-vehicles">No vehicles registered yet.</p>}
            <ul className="list-group" id="vehicle-list">
              {profile.vehicles.map((v) => (
                <li key={v.id} className="list-group-item d-flex justify-content-between align-items-center vehicle-item">
                  <div>
                    <strong>{v.make} {v.model}</strong>{v.year ? ` (${v.year})` : ''}
                    <div className="small text-muted">{v.batteryCapacityKwh} kWh · {CONNECTORS.find((c) => c.value === v.connectorType)?.label || v.connectorType}{v.licensePlate ? ` · ${v.licensePlate}` : ''}</div>
                  </div>
                  <div className="text-nowrap">
                    <button type="button" className="btn btn-sm btn-outline-secondary me-1 btn-edit-vehicle" aria-label={`Edit ${v.make} ${v.model}`} onClick={() => setEditing(v)}><i className="fas fa-edit" aria-hidden="true" /></button>
                    <button type="button" className="btn btn-sm btn-outline-danger btn-remove-vehicle" aria-label={`Remove ${v.make} ${v.model}`} onClick={() => removeVehicle(v)}><i className="fas fa-trash" aria-hidden="true" /></button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="card p-4 shadow-sm border bg-white">
            <h2 className="h5 fw-bold mb-3 text-primary"><i className="fa fa-lock me-2" aria-hidden="true" />Change Security Password</h2>
            <Flash flash={pwFlash} id="password-flash" />
            <form id="change-password-form" noValidate onSubmit={changePassword}>
              {field('pw-current', 'Current Password',
                <input id="pw-current" type="password" autoComplete="current-password" className={`form-control${pwErrors.current ? ' is-invalid' : ''}`} value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />, pwErrors.current)}
              <div className="row">
                <div className="col-md-6">
                  {field('pw-new', 'New Password',
                    <input id="pw-new" type="password" autoComplete="new-password" className={`form-control${pwErrors.next ? ' is-invalid' : ''}`} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />, pwErrors.next)}
                </div>
                <div className="col-md-6">
                  {field('pw-confirm', 'Confirm New Password',
                    <input id="pw-confirm" type="password" autoComplete="new-password" className={`form-control${pwErrors.confirm ? ' is-invalid' : ''}`} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />, pwErrors.confirm)}
                </div>
              </div>
              <button type="submit" id="btn-change-password" className="btn btn-outline-danger fw-bold btn-sm"><i className="fa fa-key me-1" aria-hidden="true" /> Update Password</button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
